/**
 * Inputs for a pe3d game schema's config fields (the engine's config/schema.ts):
 * num, bool, str, color, vec3, enum, list, table and group — laid out like
 * Version Settings: an outliner of the schema's groups over a properties panel
 * drawn with its inspector pieces (pe3dPanelKit): a SectionHeader per group,
 * FieldRows inside, a sub-group rule for groups in groups.
 *
 * The form edits the EFFECTIVE config — schema defaults with the variant's
 * patch applied — and the caller turns it back into the patch with the
 * release's own computeConfigDelta, so a variant stores only what differs from
 * the defaults, exactly like a game's variants/<name>.json.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { IconButton, MenuItem, Select, Switch, Tooltip, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import TuneIcon from "@material-ui/icons/Tune";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { BAR_BG, PANEL_BG, PROPS_PANEL_CSS, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import PlayEngineTabSearch, { matchesTabSearch } from "../playEngineTabSearch";
import {
  ColorConfigField,
  ERROR_TEXT,
  FieldProblem,
  FieldRow,
  MUTED,
  NumberConfigField,
  SectionHeader,
  StringConfigField,
} from "./pe3dPanelKit";
import { OverrideMark } from "./pe3dLanguageMark";

/** Problems for one field path: the validator writes `<path>: <message>` (and `<path>[i]…`). */
function problemsAt(problems, path) {
  return problems.filter((problem) => problem.startsWith(`${path}:`) || problem.startsWith(`${path}[`) || problem.startsWith(`${path}.`));
}

const labelOf = (name, field) => field.label || _.startCase(name);

/** The row label, with the field's help on hover. */
function Label({ text, help }) {
  if (!help) return text;
  return (
    <Tooltip title={help} placement="left">
      <span>{text}</span>
    </Tooltip>
  );
}

/** "restart" / "reload" next to a field that does not apply live (the "mixed" tag's look). */
function ApplyTag({ field }) {
  if (!field.apply || field.apply === "live") return null;
  return (
    <Tooltip title={`Changing it ${field.apply === "restart" ? "restarts the game" : `applies on ${field.apply}`}`} arrow>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#c9a25b", cursor: "default" }}>
        {field.apply}
      </span>
    </Tooltip>
  );
}

function LeafInput({ field, value, onChange, disabled }) {
  switch (field.kind) {
  case "num":
    // A number box whatever its bounds, as the playEngine version settings
    // give every number: a bounded one used to come out as a slider here, and
    // the two panels then read as two different editors for the same thing.
    return (
      <NumberConfigField
        value={value === undefined || value === null ? "" : value}
        disabled={disabled}
        onChange={(next) => onChange(field.int ? Math.round(next) : next)}
      />
    );
  case "bool":
    return <Switch size="small" color="primary" checked={Boolean(value)} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />;
  case "str":
    return <StringConfigField value={value || ""} disabled={disabled} onChange={onChange} />;
  case "color":
    return <ColorConfigField value={value || "#000000"} disabled={disabled} onCommit={onChange} />;
  case "vec3":
    return (
      <div style={{ display: "flex", gap: 4 }}>
        {["x", "y", "z"].map((axis, i) => (
          <NumberConfigField
            key={axis}
            label={axis}
            value={Array.isArray(value) ? value[i] : 0}
            disabled={disabled}
            onChange={(next) => {
              const out = Array.isArray(value) ? [...value] : [0, 0, 0];
              out[i] = next;
              onChange(out);
            }}
          />
        ))}
      </div>
    );
  case "enum":
    return (
      <Select fullWidth value={value || ""} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {field.values.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    );
  default:
    return <span style={{ fontSize: 12, color: MUTED }}>{JSON.stringify(value)}</span>;
  }
}

// A table is as wide as a FieldRow's label and control together (96 + 8 + 280,
// containerPlayEngineVersionSettings.js), so its inputs start under the labels
// and stop on the right edge every other control stops on; the remove button
// then falls where a row's action sits. 8 = the outer cell paddings, 28 = the
// remove column.
const TABLE_REMOVE_WIDTH = 28;
const TABLE_MAX_WIDTH = 96 + 8 + 280 + 8 + TABLE_REMOVE_WIDTH;

/** A blank row/item for a list or table, from the field defaults. */
function blankOf(field) {
  if (field.kind === "num") return field.min !== undefined ? field.min : 0;
  if (field.kind === "bool") return false;
  if (field.kind === "str") return "";
  if (field.kind === "color") return "#ffffff";
  if (field.kind === "vec3") return [0, 0, 0];
  if (field.kind === "enum") return field.values[0];
  return null;
}

const smallIcon = { fontSize: 14, color: MUTED };

function Problems({ problems }) {
  return problems.map((problem) => <FieldProblem key={problem}>{problem}</FieldProblem>);
}

/** A group inside a group: a 13px title on a hairline, its rows on a left rule (Custom Variables' sub-groups). */
function SubGroup({ title, children }) {
  return (
    <div style={{ margin: "8px 6px 4px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#cdd1d8" }}>{title}</span>
        <span style={{ flex: 1, height: 1, backgroundColor: SECTION_DIVIDER }} />
      </div>
      <div style={{ borderLeft: `2px solid ${SECTION_DIVIDER}`, paddingLeft: 6, marginLeft: 6 }}>{children}</div>
    </div>
  );
}

function Field({ name, path, field, value, onChange, problems, disabled, depth }) {
  const own = problemsAt(problems, path);
  // A language other than en marks the fields it sets itself (pe3dLanguageMark.js).
  const label = (
    <>
      <Label text={labelOf(name, field)} help={field.help} />
      <OverrideMark kind="config" fieldKey={path} disabled={disabled} />
    </>
  );

  if (field.kind === "group") {
    const rows = Object.entries(field.fields).map(([key, child]) => (
      <Field
        key={key}
        name={key}
        path={`${path}.${key}`}
        field={child}
        value={(value || {})[key]}
        problems={problems}
        disabled={disabled}
        depth={depth + 1}
        onChange={(next) => onChange({ ...(value || {}), [key]: next })}
      />
    ));
    if (depth === 0) {
      return (
        <>
          <SectionHeader title={labelOf(name, field)} />
          {rows}
        </>
      );
    }
    return <SubGroup title={labelOf(name, field)}>{rows}</SubGroup>;
  }

  if (field.kind === "list") {
    const items = Array.isArray(value) ? value : [];
    const canAdd = !disabled && (field.maxLength === undefined || items.length < field.maxLength);
    const canRemove = !disabled && (field.minLength === undefined || items.length > field.minLength);
    return (
      <>
        <FieldRow
          label={label}
          action={
            <Tooltip title="Add an item">
              <span>
                <IconButton size="small" disabled={!canAdd} onClick={() => onChange([...items, blankOf(field.item)])}>
                  <AddIcon style={{ fontSize: 16, color: MUTED }} />
                </IconButton>
              </span>
            </Tooltip>
          }
        >
          {items.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>Empty</span>}
          {items.map((item, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <LeafInput field={field.item} value={item} disabled={disabled} onChange={(next) => onChange(items.map((old, i) => (i === index ? next : old)))} />
              </div>
              <IconButton size="small" disabled={!canRemove} onClick={() => onChange(items.filter((_old, i) => i !== index))}>
                <CloseIcon style={smallIcon} />
              </IconButton>
            </div>
          ))}
        </FieldRow>
        <Problems problems={own} />
      </>
    );
  }

  if (field.kind === "table") {
    const rows = Array.isArray(value) ? value : [];
    const columns = Object.entries(field.columns);
    return (
      <>
        <FieldRow
          label={label}
          action={
            <Tooltip title="Add a row">
              <span>
                <IconButton size="small" disabled={disabled} onClick={() => onChange([...rows, Object.fromEntries(columns.map(([key, column]) => [key, blankOf(column)]))])}>
                  <AddIcon style={{ fontSize: 16, color: MUTED }} />
                </IconButton>
              </span>
            </Tooltip>
          }
        >
          <span style={{ fontSize: 12, color: MUTED }}>
            {rows.length} row{rows.length === 1 ? "" : "s"}
          </span>
        </FieldRow>
        {rows.length > 0 && (
          <div style={{ padding: "0 2px 6px" }}>
            <table style={{ width: "100%", maxWidth: TABLE_MAX_WIDTH, borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {columns.map(([key, column]) => (
                    <th key={key} style={{ fontSize: 11, fontWeight: 400, color: MUTED, textAlign: "left", padding: "2px 4px" }}>
                      {column.label || _.startCase(key)}
                    </th>
                  ))}
                  <th style={{ width: TABLE_REMOVE_WIDTH }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {columns.map(([key, column]) => (
                      <td key={key} style={{ padding: "2px 4px", verticalAlign: "middle" }}>
                        <LeafInput
                          field={column}
                          value={row[key]}
                          disabled={disabled}
                          onChange={(next) => onChange(rows.map((old, i) => (i === index ? { ...old, [key]: next } : old)))}
                        />
                      </td>
                    ))}
                    <td>
                      <IconButton size="small" disabled={disabled} onClick={() => onChange(rows.filter((_old, i) => i !== index))}>
                        <CloseIcon style={smallIcon} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Problems problems={own} />
      </>
    );
  }

  return (
    <>
      <FieldRow label={label} action={<ApplyTag field={field} />}>
        <LeafInput field={field} value={value} disabled={disabled} onChange={onChange} />
      </FieldRow>
      <Problems problems={own} />
    </>
  );
}


// ─── outliner + properties (Version Settings' layout) ─────────────────────

const LOOSE_NODE = "__config";
const SPLIT_STORAGE_KEY = "pe3dConfigSplit";

/**
 * The schema as an outliner tree: loose top-level fields in one "Config" node,
 * then a node per group, its groups as children. `path` is the dotted config
 * path of the group ("" for the loose node).
 */
function buildNodes(fields) {
  const entries = Object.entries(fields || {});
  const groupNodes = (pairs, parentPath) =>
    pairs
      .filter(([, field]) => field.kind === "group")
      .map(([key, field]) => {
        const path = parentPath ? `${parentPath}.${key}` : key;
        return { id: path, path, label: labelOf(key, field), field, children: groupNodes(Object.entries(field.fields), path) };
      });
  const loose = entries.filter(([, field]) => field.kind !== "group");
  const nodes = [];
  if (loose.length > 0) nodes.push({ id: LOOSE_NODE, path: "", label: "Config", field: { kind: "group", fields: Object.fromEntries(loose) }, children: [] });
  return nodes.concat(groupNodes(entries, ""));
}

const flattenNodes = (nodes) => nodes.flatMap((node) => [node, ...flattenNodes(node.children)]);

/** A node matches when its name, or the name or key of any field under it, does. */
function nodeMatches(node, query) {
  if (matchesTabSearch(node.label, query)) return true;
  const walk = (fields) =>
    Object.entries(fields).some(([key, field]) => matchesTabSearch(key, query) || matchesTabSearch(labelOf(key, field), query) || (field.kind === "group" && walk(field.fields)));
  return walk(node.field.fields);
}

function filterNodes(nodes, query) {
  if (!query.trim()) return nodes;
  return nodes
    .map((node) => ({ ...node, children: filterNodes(node.children, query) }))
    .filter((node) => node.children.length > 0 || nodeMatches(node, query));
}

const nodeFieldCount = (node) => Object.values(node.field.fields).filter((field) => field.kind !== "group").length;
const valueAt = (config, path) => (path ? _.get(config, path) : config);

/** The outliner row: Version Settings' component row, a group icon instead of the component one. */
function NodeLabel({ node, hasProblems }) {
  const count = nodeFieldCount(node);
  return (
    <div style={{ display: "flex", alignItems: "center", minHeight: 22, flex: 1, minWidth: 0 }}>
      <TuneIcon style={{ fontSize: 13, color: "#8a8a8a", marginLeft: 16, marginRight: 6, flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
        <span style={{ fontWeight: 600, color: "#e0e0e0" }}>{node.label}</span>
        {count > 0 && <span style={{ color: "#7d7d7d" }}> ({count})</span>}
      </span>
      {hasProblems && <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: ERROR_TEXT, marginLeft: 6, flexShrink: 0 }} />}
    </div>
  );
}

const TREE_SX = {
  overflowX: "hidden",
  flexGrow: 1,
  marginTop: "8px",
  "& .MuiTreeItem-content": { padding: "0 4px", borderRadius: 0, minHeight: "22px", gap: 0 },
  "& .MuiTreeItem-content:hover": { backgroundColor: "rgba(255, 255, 255, 0.055)" },
  "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused": { backgroundColor: "rgba(144, 202, 249, 0.20)" },
  "& .MuiTreeItem-content.Mui-selected:hover": { backgroundColor: "rgba(144, 202, 249, 0.26)" },
  "& .MuiTreeItem-content.Mui-focused": { backgroundColor: "transparent" },
  "& .MuiTreeItem-iconContainer": { width: "16px", marginRight: 0, flexShrink: 0, "& svg": { fontSize: 18, color: "#8a8a8a" } },
  "& .MuiTreeItem-label": { paddingLeft: 0 },
  "& .MuiTreeItem-group": { marginLeft: "9px", paddingLeft: "6px", borderLeft: "1px solid rgba(255, 255, 255, 0.09)" },
};

/**
 * Laid out like Version Settings (containerPlayEngineVersionSettings.js) but
 * its own component: a search bar, the outliner (the schema's groups) on top,
 * a draggable divider, and the properties panel with the selected group's
 * fields under a sticky header.
 *
 * @param props.fields    schema.config
 * @param props.config    the effective config
 * @param props.onChange  (nextEffectiveConfig) => void
 * @param props.problems  validator messages
 * @param props.disabled
 */
export default function Pe3dConfigFields({ fields, config, onChange, problems, disabled }) {
  const containerRef = useRef(null);
  const [search, setSearch] = useState("");
  const [selectedID, setSelectedID] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [treeRatio, setTreeRatio] = useState(() => {
    let raw = NaN;
    try {
      raw = Number(window.localStorage.getItem(SPLIT_STORAGE_KEY));
    } catch (e) {
      // no storage: the default split
    }
    return isFinite(raw) && raw >= 0.2 && raw <= 0.8 ? raw : 0.4;
  });

  const nodes = useMemo(() => buildNodes(fields), [fields]);
  const allNodes = useMemo(() => flattenNodes(nodes), [nodes]);
  const shown = useMemo(() => filterNodes(nodes, search), [nodes, search]);
  const allProblems = problems || [];

  // Keep a group selected: the first one, until the user picks another.
  useEffect(() => {
    if (!allNodes.some((node) => node.id === selectedID)) setSelectedID(allNodes.length > 0 ? allNodes[0].id : null);
  }, [allNodes, selectedID]);

  const selected = allNodes.find((node) => node.id === selectedID) || null;
  const expandedItems = search.trim() ? flattenNodes(shown).map((node) => node.id) : expanded;

  const problemsUnder = (node) =>
    node.id === LOOSE_NODE
      ? Object.keys(node.field.fields).some((key) => problemsAt(allProblems, key).length > 0)
      : problemsAt(allProblems, node.path).length > 0;

  function startDividerDrag(event) {
    event.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    function onMove(moveEvent) {
      const rect = container.getBoundingClientRect();
      if (rect.height <= 0) return;
      setTreeRatio(Math.max(0.2, Math.min(0.8, (moveEvent.clientY - rect.top) / rect.height)));
    }
    function onUp() {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = prevUserSelect;
      setTreeRatio((ratio) => {
        try {
          window.localStorage.setItem(SPLIT_STORAGE_KEY, String(ratio));
        } catch (e) {
          // not remembered, nothing else
        }
        return ratio;
      });
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  const renderNode = (node) => (
    <TreeItem key={node.id} itemId={node.id} label={<NodeLabel node={node} hasProblems={problemsUnder(node)} />}>
      {node.children.length > 0 ? node.children.map(renderNode) : null}
    </TreeItem>
  );

  // The selected group's own fields under "Config", its groups as sections of their own.
  const renderProperties = (node) => {
    const value = valueAt(config || {}, node.path);
    const setValue = (next) => onChange(node.path ? _.set(_.cloneDeep(config || {}), node.path, next) : next);
    const entries = Object.entries(node.field.fields);
    const renderField = (depth, [key, field]) => (
      <Field
        key={key}
        name={key}
        path={node.path ? `${node.path}.${key}` : key}
        field={field}
        value={(value || {})[key]}
        problems={allProblems}
        disabled={disabled}
        depth={depth}
        onChange={(next) => setValue({ ...(value || {}), [key]: next })}
      />
    );
    const leaves = entries.filter(([, field]) => field.kind !== "group");
    const groups = entries.filter(([, field]) => field.kind === "group");
    return (
      <div style={{ paddingBottom: 8 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 10px",
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
            backgroundColor: BAR_BG,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <TuneIcon style={{ fontSize: 16, color: "#8a8a8a", marginRight: 8, flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#e6e6e6", fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>{node.label}</span>
            <span style={{ color: MUTED }}> · {node.path || "config"}</span>
          </span>
        </div>
        {leaves.length > 0 && (
          <>
            <SectionHeader title="Config" />
            {leaves.map((entry) => renderField(1, entry))}
          </>
        )}
        {groups.map((entry) => renderField(0, entry))}
      </div>
    );
  };

  return (
    <div ref={containerRef} style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden", backgroundColor: PANEL_BG }}>
      <PlayEngineTabSearch value={search} onChange={setSearch} />

      {/* Outliner — the schema's groups */}
      <div className="proto-dark-scroll" style={{ flexGrow: treeRatio, flexShrink: 1, flexBasis: 0, minHeight: 0, overflowY: "auto" }}>
        {nodes.length === 0 && <Typography style={{ color: "#888", padding: "8px 12px", fontSize: 12 }}>This game has no config fields.</Typography>}
        {nodes.length > 0 && shown.length === 0 && (
          <Typography style={{ color: "#888", padding: "8px 12px", fontSize: 12 }}>No groups or fields match this search.</Typography>
        )}
        {shown.length > 0 && (
          <SimpleTreeView
            aria-label="pe3d-config-groups"
            expandedItems={expandedItems}
            onExpandedItemsChange={(event, itemIds) => setExpanded(itemIds)}
            selectedItems={selectedID}
            onSelectedItemsChange={(event, itemID) => itemID && setSelectedID(itemID)}
            expansionTrigger="iconContainer"
            slots={{ expandIcon: ArrowRightIcon, collapseIcon: ArrowDropDownIcon }}
            sx={TREE_SX}
          >
            {shown.map(renderNode)}
          </SimpleTreeView>
        )}
      </div>

      {/* Draggable divider between the outliner and the properties panel */}
      <div
        onPointerDown={startDividerDrag}
        style={{ flex: "0 0 auto", height: 6, cursor: "row-resize", backgroundColor: "#2b2b2b", borderTop: "1px solid #1f1f1f", borderBottom: "1px solid #1f1f1f" }}
      />

      {/* Properties panel — the selected group's fields */}
      <div className="proto-props-panel proto-dark-scroll" style={{ flexGrow: 1 - treeRatio, flexShrink: 1, flexBasis: 0, minHeight: 0, overflowY: "auto", backgroundColor: PANEL_BG }}>
        <style>{PROPS_PANEL_CSS}</style>
        {selected ? (
          renderProperties(selected)
        ) : (
          <Typography variant="body2" style={{ color: "#777", textAlign: "center", padding: "24px 16px", display: "block" }}>
            Select a group to edit its settings
          </Typography>
        )}
      </div>
    </div>
  );
}
